// Deterministic ID derivation.
//
// Today bookings and reviews use Firestore auto-IDs and enforce uniqueness
// with a query-then-write check (see bookingService.js, functions/index.js
// submitReview), which is race-prone under concurrent requests. These
// derivations give later phases a deterministic doc ID to key off instead,
// so uniqueness can be enforced by the write itself (e.g. a transaction
// `create`) rather than a preceding query.

const { ValidationError } = require('../errors');

const requireAll = (fields, label) => {
  const missing = Object.entries(fields).filter(([, value]) => !value);
  if (missing.length > 0) {
    throw new ValidationError(`Cannot derive ${label} id: missing ${missing.map(([key]) => key).join(', ')}`);
  }
};

// A guest may hold at most one confirmed booking per meal.
const deriveBookingId = ({ mealId, guestId }) => {
  requireAll({ mealId, guestId }, 'booking');
  return `${mealId}_${guestId}`;
};

// A booking has at most one review per role (host, guest).
const deriveReviewId = ({ bookingId, reviewerRole }) => {
  requireAll({ bookingId, reviewerRole }, 'review');
  return `${bookingId}_${reviewerRole}`;
};

// The users collection is already keyed by Firebase Auth uid; this exists
// so callers never have to reach for the literal `uid` themselves.
const deriveUserId = (authUid) => {
  requireAll({ authUid }, 'user');
  return authUid;
};

// A contact grant is symmetric (either party may read the other's private
// contact info once it exists), so the id is sorted to collapse both
// orderings of the pair onto one doc.
const deriveContactGrantId = ({ uidA, uidB }) => {
  requireAll({ uidA, uidB }, 'contact grant');
  return [uidA, uidB].sort().join('_');
};

module.exports = { deriveBookingId, deriveReviewId, deriveUserId, deriveContactGrantId };
